import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, shape, string } from 'prop-types'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import './VoucherBox.scss'
import { TENANT_AT, TENANT_COM } from 'shared/consts'
import { addVoucherAction, deleteVoucherAction } from 'shared/store/ducks/cart'
import {
  resetVoucherAction,
  resetVoucherErrorAction,
} from 'shared/store/ducks/cart/actions'
import { withI18n } from 'shared/utils/withI18n'
import Button from 'views/components/atoms/Button'
import IconBin from 'views/components/atoms/Icon/IconBin'
import IconTick from 'views/components/atoms/Icon/IconTick'
import LoadingIndicator from 'views/components/atoms/LoadingIndicator'
import FormControlWithFloatingLabel from 'views/components/molecules/FormControlWithFloatingLabel'
import { VOUCHER_TYPE } from 'views/consts'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import { i18nType } from 'views/utils/propTypes'

const VoucherBox = props => {
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const { vouchers, nonDiscountableVouchers, i18n, promotionInformation } =
    props
  const [voucherCode, setVoucherCode] = useState(null)
  const dispatch = useDispatch()
  const voucherLoad = useSelector(state => state.cart.voucherLoad)
  const accepted = useSelector(state => state.cart.voucherAccepted)
  const error = useSelector(state => state.cart.voucherError)
  const promotionErrorMessage = useSelector(
    state => state.cart.promotionErrorMessage
  )

  const mapVoucherToStatusMessage = promotionInformation
    .filter(info => Boolean(info.coupon))
    .reduce((map, info) => {
      map[info.coupon] = info.rules
        .flatMap(rule =>
          rule.message ? rule.message : rule.conditions.map(c => c.message)
        )
        .filter(Boolean)
        .join(' - ')
      return map
    }, {})

  const VOUCHERS_WITH_GENERIC_ERROR_MESSAGE = [
    {
      code: 'servus',
      minimumAmount: '39,00',
    },
    {
      code: 'nl',
      minimumAmount: '60,00',
      startsWith: true,
    },
    {
      code: 'appat5',
      minimumAmount: '50,00',
    },
    {
      code: 'at22',
      startsWith: true,
    },
    {
      code: 'nwl',
      minimumAmount: '60,00',
      startsWith: true,
    },
    {
      code: 'neu10',
      minimumAmount: '35,00',
    },
    {
      code: 'inflation2022',
      minimumAmount: '49,00',
    },
    {
      code: 'inflation22at',
      minimumAmount: '69,00',
    },
    {
      code: 'appde5',
      minimumAmount: '50,00',
    },
    {
      code: 'ea220',
      startsWith: true,
    },
    {
      code: 'de122',
      startsWith: true,
    },
    {
      code: 'de222',
      startsWith: true,
    },
    {
      code: 'de322',
      startsWith: true,
    },
    {
      code: '5sa',
      startsWith: true,
    },
    {
      code: 'smartdays22',
      startsWith: true,
      minimumAmount: '59,00',
    },
    {
      code: 'smartdays2022',
      startsWith: true,
      minimumAmount: '69,00',
    },
  ]

  const getGenericVoucherError = (minimumAmount = '') => {
    const minimumAmountText = minimumAmount ? `von ${minimumAmount}€ ` : ''

    return `Dieser Gutschein kann leider nicht angewendet werden, da er nicht mit den Produkten in Ihrem Warenkorb kombinierbar ist oder der Mindestbestellwert ${minimumAmountText}nicht erreicht wurde. Weitere Informationen zu den Gutscheinbedingungen finden Sie in den Fußnoten des jeweiligen Gutscheinkanals (z. B. Website, App, Newsletter, Mailing oder Partnerwebsite).`
  }

  let buttonStyle = 'buttonGhost'

  if (voucherLoad) {
    buttonStyle = 'buttonLoad'
  }
  if (accepted) {
    buttonStyle = 'buttonAccepted'

    if (voucherCode) {
      setVoucherCode('')
    }
  }

  const handleInputChange = e => {
    if (error) {
      dispatch(resetVoucherErrorAction())
    }
    if (accepted) {
      dispatch(resetVoucherAction())
    }
    setVoucherCode(e.target.value)
  }

  const handleInputKeyUp = e => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      dispatch(addVoucherAction(voucherCode))
    }
  }

  /**
   * renderVoucherDeletionLink
   * function for render the deletion link for Vouchers shown in the voucher box
   * @param {*} currentVoucherCode the vouchercode which should be rendered
   * @param {*} statusMessage extract from promotionInformation. shows additional info for that coupon
   * @returns markup for the deletionLink
   */
  const renderVoucherDeletionLink = (currentVoucherCode, statusMessage) => {
    return (
      <div
        key={currentVoucherCode}
        className="o-VoucherBox--VoucherItem l-flex u-margin-small--bottom"
      >
        <span className="o-VoucherBox--VoucherItem__text">
          {currentVoucherCode}
          {statusMessage ? ' - ' + statusMessage : null}
        </span>
        <div
          data-qa-id={`voucherBox-${currentVoucherCode}-delete`}
          onKeyDown={() => dispatch(deleteVoucherAction(currentVoucherCode))}
          onClick={() => dispatch(deleteVoucherAction(currentVoucherCode))}
          className="o-VoucherBox--VoucherItem__bin"
          role="button"
          tabIndex={0}
        >
          <IconBin size="small" />
        </div>
      </div>
    )
  }

  // Voucher Translation map
  const errorMessage = {
    'couponnotfound': i18n._('voucherBox.error.CouponNotFound'),
    'couponstartdateinfuture': i18n._(
      'voucherBox.error.CouponStartDateInFuture'
    ),
    'couponexpired': i18n._('voucherBox.error.CouponExpired'),
    'couponpartofnotrunningcampaign': i18n._(
      'voucherBox.error.CouponPartOfNotRunningCampaign'
    ),
    'profilelimitreached': i18n._('voucherBox.error.ProfileLimitReached'),
    'couponlimitreached': i18n._('voucherBox.error.CouponLimitReached'),
    'campaignlimitreached': i18n._('voucherBox.error.CampaignLimitReached'),
    'couponrecipientdoesnotmatch': i18n._(
      'voucherBox.error.CouponRecipientDoesNotMatch'
    ),
    'couponrejectedbycondition': i18n._(
      'voucherBox.error.CouponRejectedByCondition'
    ),
    'effectcouldnotbeapplied': i18n._(
      'voucherBox.error.EffectCouldNotBeApplied'
    ),
    'couponvalidconditionmissing': i18n._(
      'voucherBox.error.CouponValidConditionMissing'
    ),
    'erxvouchererrorerxitemnotfoundincart': i18n._(
      'voucherBox.error.ERxVoucherErrorERxItemNotFoundInCart',
      { voucher: voucherCode }
    ),
    'erxvouchererroronlymiraklproductsincart': i18n._(
      'voucherBox.error.ERxVoucherErrorOnlyMiraklProductsInCart',
      { voucher: voucherCode }
    ),
    'erxvouchererrorsaeselleritemnotfoundincart': i18n._(
      'voucherBox.error.ERxVoucherErrorSaeSellerItemNotFoundInCart',
      { voucher: voucherCode }
    ),
  }[error ? error.toLowerCase() : '']

  const voucher = VOUCHERS_WITH_GENERIC_ERROR_MESSAGE.find(voucher =>
    voucher.startsWith
      ? voucherCode?.startsWith(voucher.code)
      : voucherCode === voucher.code
  )

  const OLD_MESSAGE_KEY = 'CouponRejectedByCondition'

  const finalErrorMessage =
    [TENANT_COM, TENANT_AT].includes(tenant) && error === OLD_MESSAGE_KEY
      ? getGenericVoucherError(voucher?.minimumAmount)
      : errorMessage

  const buttonClassNames = classNames('o-VoucherBox--Button l-flex__start', {
    'o-VoucherBox--Button-accepted': accepted,
  })

  return (
    <div className="o-VoucherBox l-flex l-flex__column">
      <div className="o-VoucherBox--InputRow l-flex">
        <FormControlWithFloatingLabel
          inputClassname="o-VoucherBox--Input"
          name="voucherBox-input"
          value={voucherCode}
          onChange={handleInputChange}
          errorMessage={promotionErrorMessage || finalErrorMessage || ''}
          onKeyUp={handleInputKeyUp}
        >
          <Trans id="voucherBox.input.label.placeholder" />
        </FormControlWithFloatingLabel>
        <Button
          className={buttonClassNames}
          name="voucherBox-submit"
          style={buttonStyle}
          onClick={() => dispatch(addVoucherAction(voucherCode))}
        >
          {!accepted && !voucherLoad && (
            <Trans id="voucherBox.button.label.redeemVoucherShort" />
          )}

          {accepted && <IconTick />}
          {voucherLoad && <LoadingIndicator color="white" />}
        </Button>
      </div>
      <div className="o-VoucherBox--Vouchers l-flex l-flex--wrap">
        {vouchers.map(
          voucher =>
            voucher.type === VOUCHER_TYPE.VOUCHER &&
            renderVoucherDeletionLink(
              voucher.code,
              mapVoucherToStatusMessage[voucher.code]
            )
        )}
        {nonDiscountableVouchers.map(voucher =>
          renderVoucherDeletionLink(voucher, mapVoucherToStatusMessage[voucher])
        )}
      </div>
    </div>
  )
}

VoucherBox.propTypes = {
  nonDiscountableVouchers: arrayOf(string),
  vouchers: arrayOf(shape({})),
  promotionInformation: arrayOf(shape({})),
  i18n: i18nType.isRequired,
  publicRuntimeConfig: shape({}),
}

VoucherBox.defaultProps = {
  nonDiscountableVouchers: [],
  vouchers: [],
  promotionInformation: [],
}

export default withI18n()(VoucherBox)
